(() => {
  const cleanups = new WeakMap();

  const ready = (callback) => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
    } else {
      callback();
    }
  };

  const initializeHero = (hero) => {
    if (hero.dataset.cwHomeInitialized === 'true') return;
    hero.dataset.cwHomeInitialized = 'true';

    const controller = new AbortController();
    const { signal } = controller;
    const slides = Array.from(hero.querySelectorAll('[data-cw-home-slide]'));
    const dots = Array.from(hero.querySelectorAll('[data-cw-home-hero-goto]'));
    const previous = hero.querySelector('[data-cw-home-hero-prev]');
    const next = hero.querySelector('[data-cw-home-hero-next]');
    // Match the media visibility breakpoint; the app header still changes at 1100px.
    const mobileViewport = window.matchMedia('(max-width: 640px)');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const delay = Number(hero.dataset.autoplay) || 6500;
    const bounds = hero.getBoundingClientRect();
    let inView = bounds.bottom > 0 && bounds.top < window.innerHeight;
    let pageActive = true;
    let pointerInside = false;
    let activeIndex = 0;
    let timer;
    const videoStates = slides.flatMap((slide) => Array.from(slide.querySelectorAll('video'), (video) => ({
      video,
      sources: Array.from(video.querySelectorAll('source')),
      failedSources: new Set(),
      active: false,
      failed: false,
      attempt: 0
    })));

    const preferredVideo = (slide) => {
      const videos = Array.from(slide.querySelectorAll('video'));
      const preferredClass = mobileViewport.matches
        ? 'cw-home-hero__media--mobile'
        : 'cw-home-hero__media--desktop';
      return videos.find((video) => video.classList.contains(preferredClass)) || videos[0];
    };

    const stopVideo = (state) => {
      state.active = false;
      state.attempt += 1;
      state.video.pause();
      state.video.preload = 'none';
      if (state.sources.some((source) => source.hasAttribute('src'))) {
        state.sources.forEach((source) => source.removeAttribute('src'));
        // Release the decoder/download and restore the native poster.
        state.video.load();
      }
    };

    const failVideo = (state, attempt) => {
      if (!state.active || state.attempt !== attempt) return;
      state.failed = true;
      stopVideo(state);
    };

    const startVideo = (state) => {
      if (state.active || state.failed || !state.sources.some((source) => source.dataset.src)) return;
      state.active = true;
      const attempt = ++state.attempt;
      state.failedSources.clear();
      state.sources.forEach((source) => {
        if (source.dataset.src) source.setAttribute('src', source.dataset.src);
      });
      state.video.muted = true;
      state.video.preload = 'auto';
      state.video.load();
      state.video.play()?.catch(() => failVideo(state, attempt));
    };

    const canPlay = () => pageActive && inView && !document.hidden && !reducedMotion.matches;
    const syncVideo = () => {
      const selected = canPlay() && slides[activeIndex] ? preferredVideo(slides[activeIndex]) : null;
      // Stop the obsolete variant before starting its replacement.
      videoStates.forEach((state) => {
        if (state.active && state.video !== selected) stopVideo(state);
      });
      const state = videoStates.find((candidate) => candidate.video === selected);
      if (state) startVideo(state);
    };
    videoStates.forEach((state) => {
      state.video.addEventListener('error', () => {
        if (state.video.error) failVideo(state, state.attempt);
      }, { signal });
      // Browsers can leave play() pending when every <source> fails, without
      // dispatching an error on <video>. Keep alternative sources available.
      state.sources.forEach((source) => {
        source.addEventListener('error', () => {
          if (!state.active) return;
          state.failedSources.add(source);
          if (state.sources.every((candidate) => !candidate.dataset.src || state.failedSources.has(candidate))) {
            failVideo(state, state.attempt);
          }
        }, { signal });
      });
    });

    const syncHeight = () => {
      const top = Math.max(0, Math.round(hero.getBoundingClientRect().top + window.scrollY));
      const appBarOffset = window.matchMedia('(max-width: 1100px)').matches
        ? 'calc(var(--cw-app-bar-height, 60px) + var(--cw-app-safe-bottom, 0px))'
        : '0px';
      hero.style.setProperty(
        '--cw-home-hero-height',
        `max(520px, calc(100svh - ${top}px - ${appBarOffset}))`
      );
    };

    const show = (index) => {
      activeIndex = (index + slides.length) % slides.length;
      if (activeIndex !== 0) hero.classList.remove('cw-home-hero--initial');
      slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === activeIndex;
        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', String(!isActive));
      });
      dots.forEach((dot, dotIndex) => dot.classList.toggle('is-active', dotIndex === activeIndex));
      syncVideo();
    };

    const start = () => {
      window.clearInterval(timer);
      if (slides.length > 1 && canPlay() && !pointerInside) {
        timer = window.setInterval(() => show(activeIndex + 1), delay);
      }
    };
    const syncPlayback = () => {
      syncVideo();
      start();
    };

    previous?.addEventListener('click', () => {
      show(activeIndex - 1);
      start();
    }, { signal });
    next?.addEventListener('click', () => {
      show(activeIndex + 1);
      start();
    }, { signal });
    dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        show(Number(dot.dataset.cwHomeHeroGoto));
        start();
      }, { signal });
    });
    hero.addEventListener('pointerenter', (event) => {
      if (event.pointerType === 'touch') return;
      pointerInside = true;
      window.clearInterval(timer);
    }, { signal });
    hero.addEventListener('pointerleave', () => {
      pointerInside = false;
      start();
    }, { signal });
    document.addEventListener('visibilitychange', syncPlayback, { signal });
    reducedMotion.addEventListener('change', syncPlayback, { signal });
    window.addEventListener('pagehide', () => {
      pageActive = false;
      syncPlayback();
    }, { signal });
    window.addEventListener('pageshow', () => {
      pageActive = true;
      syncPlayback();
    }, { signal });
    window.addEventListener('resize', syncHeight, { passive: true, signal });
    window.visualViewport?.addEventListener('resize', syncHeight, { passive: true, signal });
    mobileViewport.addEventListener('change', () => {
      show(activeIndex);
      syncHeight();
    }, { signal });
    const header = document.querySelector('.shopify-section--header');
    const headerObserver = header && 'ResizeObserver' in window
      ? new ResizeObserver(syncHeight)
      : null;
    headerObserver?.observe(header);
    const visibilityObserver = 'IntersectionObserver' in window
      ? new IntersectionObserver((entries) => {
        inView = entries[0].isIntersecting;
        syncPlayback();
      })
      : null;
    visibilityObserver?.observe(hero);

    syncHeight();
    window.requestAnimationFrame(() => window.requestAnimationFrame(syncHeight));
    window.addEventListener('load', syncHeight, { once: true, signal });
    show(0);
    start();

    cleanups.set(hero, () => {
      controller.abort();
      pageActive = false;
      window.clearInterval(timer);
      headerObserver?.disconnect();
      visibilityObserver?.disconnect();
      videoStates.forEach(stopVideo);
      hero.removeAttribute('data-cw-home-initialized');
    });
  };

  const initializeYoutube = (button) => {
    if (button.dataset.cwHomeInitialized === 'true') return;
    button.dataset.cwHomeInitialized = 'true';

    button.addEventListener('click', () => {
      const videoId = button.dataset.videoId;
      if (!videoId) return;

      const iframe = document.createElement('iframe');
      iframe.src = `https://www.youtube-nocookie.com/embed/${encodeURIComponent(videoId)}?autoplay=1&rel=0`;
      iframe.title = 'Cologne Watch YouTube Video';
      iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
      iframe.allowFullscreen = true;
      button.replaceChildren(iframe);
      button.removeAttribute('data-cw-home-youtube');
    }, { once: true });
  };

  const initializeVideoShowcase = (showcase) => {
    if (showcase.dataset.cwHomeInitialized === 'true') return;

    const track = showcase.querySelector('[data-cw-home-showcase-track]');
    const cards = Array.from(showcase.querySelectorAll('[data-cw-home-showcase-card]'));
    const previous = showcase.querySelector('[data-cw-home-showcase-prev]');
    const next = showcase.querySelector('[data-cw-home-showcase-next]');
    const dialog = showcase.querySelector('[data-cw-home-showcase-dialog]');
    const dialogVideo = showcase.querySelector('[data-cw-home-showcase-dialog-video]');
    const dialogPrevious = showcase.querySelector('[data-cw-home-showcase-dialog-prev]');
    const dialogNext = showcase.querySelector('[data-cw-home-showcase-dialog-next]');
    const dialogClose = showcase.querySelector('[data-cw-home-showcase-close]');
    if (!track || !cards.length || !previous || !next || !dialog || !dialogVideo) return;

    showcase.dataset.cwHomeInitialized = 'true';
    const controller = new AbortController();
    const { signal } = controller;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let activeDialogIndex = 0;

    const previewVideo = (card) => card.querySelector('video');
    const videoSource = (video) => (
      video?.currentSrc || video?.src || video?.querySelector('source')?.src || ''
    );
    const setHidden = (button, hidden) => {
      button.classList.toggle('is-hidden', hidden);
      button.disabled = hidden;
      button.setAttribute('aria-hidden', String(hidden));
    };
    const updateRail = () => {
      const end = Math.max(0, track.scrollWidth - track.clientWidth);
      const hasOverflow = end > 2;
      setHidden(previous, !hasOverflow || track.scrollLeft <= 2);
      setHidden(next, !hasOverflow || track.scrollLeft >= end - 2);
    };
    const moveRail = (direction) => {
      if (window.matchMedia('(max-width: 640px)').matches) {
        const current = cards.reduce((best, card, index) => {
          const distance = Math.abs(card.offsetLeft - track.scrollLeft);
          return distance < best.distance ? { index, distance } : best;
        }, { index: 0, distance: Number.POSITIVE_INFINITY }).index;
        const target = Math.max(0, Math.min(current + direction, cards.length - 1));
        track.scrollTo({ left: cards[target].offsetLeft, behavior: 'smooth' });
        return;
      }

      track.scrollBy({
        left: direction * Math.max(track.clientWidth * .85, 240),
        behavior: 'smooth'
      });
    };
    const updateDialogNavigation = () => {
      if (dialogPrevious) dialogPrevious.disabled = activeDialogIndex <= 0;
      if (dialogNext) dialogNext.disabled = activeDialogIndex >= cards.length - 1;
    };
    const showDialogVideo = (index) => {
      const targetIndex = Math.max(0, Math.min(index, cards.length - 1));
      const sourceVideo = previewVideo(cards[targetIndex]);
      const source = videoSource(sourceVideo);
      if (!source) return;

      activeDialogIndex = targetIndex;
      dialogVideo.pause();
      dialogVideo.src = source;
      dialogVideo.poster = sourceVideo.poster || '';
      dialogVideo.setAttribute('aria-label', cards[targetIndex].getAttribute('aria-label') || dialog.dataset.videoLabel || '');
      updateDialogNavigation();
      dialogVideo.load();
      dialogVideo.play().catch(() => {});
    };
    const openDialog = (index) => {
      showDialogVideo(index);
      if (!dialog.open) dialog.showModal();
    };
    const closeDialog = () => {
      dialogVideo.pause();
      dialogVideo.removeAttribute('src');
      dialogVideo.removeAttribute('poster');
      dialogVideo.load();
      if (dialog.open) dialog.close();
    };

    previous.addEventListener('click', () => moveRail(-1), { signal });
    next.addEventListener('click', () => moveRail(1), { signal });
    track.addEventListener('scroll', updateRail, { passive: true, signal });
    window.addEventListener('resize', updateRail, { passive: true, signal });
    cards.forEach((card, index) => {
      card.addEventListener('click', () => openDialog(index), { signal });
    });
    dialogPrevious?.addEventListener('click', () => showDialogVideo(activeDialogIndex - 1), { signal });
    dialogNext?.addEventListener('click', () => showDialogVideo(activeDialogIndex + 1), { signal });
    dialogClose?.addEventListener('click', closeDialog, { signal });
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) closeDialog();
    }, { signal });
    dialog.addEventListener('close', () => {
      dialogVideo.pause();
      dialogVideo.removeAttribute('src');
      dialogVideo.load();
    }, { signal });

    const visibilityObserver = 'IntersectionObserver' in window
      ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const video = previewVideo(entry.target);
          if (!video) return;
          if (entry.isIntersecting && entry.intersectionRatio >= .45 && !reducedMotion.matches) {
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        });
      }, { threshold: [.1, .45, .8] })
      : null;
    cards.forEach((card) => visibilityObserver?.observe(card));

    const resizeObserver = 'ResizeObserver' in window ? new ResizeObserver(updateRail) : null;
    resizeObserver?.observe(track);
    updateRail();

    cleanups.set(showcase, () => {
      controller.abort();
      visibilityObserver?.disconnect();
      resizeObserver?.disconnect();
      cards.forEach((card) => previewVideo(card)?.pause());
      closeDialog();
      showcase.removeAttribute('data-cw-home-initialized');
    });
  };

  const initializeReviews = (carousel) => {
    if (carousel.dataset.cwHomeInitialized === 'true') return;

    const track = carousel.querySelector('[data-cw-home-reviews-track]');
    const previous = carousel.querySelector('[data-cw-home-reviews-prev]');
    const next = carousel.querySelector('[data-cw-home-reviews-next]');
    if (!track || !previous || !next) return;

    carousel.dataset.cwHomeInitialized = 'true';
    const controller = new AbortController();
    const { signal } = controller;
    const update = () => {
      const end = Math.max(0, track.scrollWidth - track.clientWidth);
      previous.classList.toggle('is-hidden', track.scrollLeft <= 2);
      next.classList.toggle('is-hidden', track.scrollLeft >= end - 2);
    };
    const move = (direction) => {
      const card = track.querySelector('.cw-home-review');
      const gap = Number.parseFloat(getComputedStyle(track).columnGap) || 0;
      track.scrollBy({
        left: direction * ((card?.getBoundingClientRect().width || track.clientWidth) + gap),
        behavior: 'smooth'
      });
    };

    previous.addEventListener('click', () => move(-1), { signal });
    next.addEventListener('click', () => move(1), { signal });
    track.addEventListener('scroll', update, { passive: true, signal });
    window.addEventListener('resize', update, { passive: true, signal });
    update();

    cleanups.set(carousel, () => {
      controller.abort();
      carousel.removeAttribute('data-cw-home-initialized');
    });
  };

  const initialize = (root = document) => {
    root.querySelectorAll('[data-cw-home-hero]').forEach(initializeHero);
    root.querySelectorAll('[data-cw-home-youtube]').forEach(initializeYoutube);
    root.querySelectorAll('[data-cw-home-showcase]').forEach(initializeVideoShowcase);
    root.querySelectorAll('[data-cw-home-reviews]').forEach(initializeReviews);
  };

  const destroy = (root) => {
    root.querySelectorAll(
      '[data-cw-home-hero], [data-cw-home-showcase], [data-cw-home-reviews]'
    ).forEach((element) => {
      cleanups.get(element)?.();
      cleanups.delete(element);
    });
  };

  ready(() => {
    initialize();
    document.addEventListener('shopify:section:load', (event) => initialize(event.target));
    document.addEventListener('shopify:section:unload', (event) => destroy(event.target));
  });
})();
